/**
 * What a property may be called.
 *
 * Named `vocabulary.ts` and not `keys.ts`: `src/api/keys.ts` already exists and
 * is the home of TanStack query keys, which CLAUDE.md names. Two files called
 * keys.ts meaning unrelated things is a search and a conversation both going
 * wrong later.
 *
 * **This is an allow-list, and that is the whole design.** Two earlier versions
 * were deny-lists — a set of forbidden words — and web-tester defeated both by
 * inspection: the first leaked 41 of 44 sensitive payloads, and the second,
 * which had already absorbed one round of corrections, still leaked 7. Neither
 * round failed for want of a word. `card`, `ssn`, `threshold: '118.4'` and a
 * nested object were never going to be on any list somebody wrote in advance,
 * because the space of names a developer might reach for is unbounded and the
 * list is finite.
 *
 * The plan's own framing is the requirement: *"the risk here is sending a
 * merchant name or an amount to a third party. That must be impossible by
 * construction, not by review."* A deny-list cannot deliver that — it is review,
 * performed in advance, by someone who had to guess. An allow-list can: a
 * property that nobody registered does not go out, whatever it is called.
 *
 * The cost is real and is the point. Adding a property to an event is a one-line
 * change here, made deliberately, by someone who has to look at this file and
 * its comment. That is the same trade `ENTITLEMENTS` makes — seven names, fixed,
 * where inventing an eighth is a defect rather than a convenience.
 */

/**
 * Final segments that turn a stem into a measurement OF that stem.
 *
 * A LAST segment, never a suffix matched anywhere in the key: testing
 * `key.endsWith('_bucket')` is what let `merchant_name_bucket` through twice.
 */
export const MEASUREMENTS = new Set([
  'count',
  'length',
  'ms',
  'seconds',
  'minutes',
  'hours',
  'days',
  'index',
  'bucket',
  'position',
  'depth',
  'rate',
  'ratio',
  'pct',
  'percent',
])

/** Measurements that may carry a fractional value. Everything else numeric must
 *  be a whole number, because money is integer cents (CLAUDE.md). */
export const FRACTIONAL = new Set(['rate', 'ratio', 'pct', 'percent'])

/** Final segments that make a key a boolean state rather than data. */
export const FLAGS = new Set([
  'active',
  'enabled',
  'shown',
  'visible',
  'present',
  'used',
  'applied',
  'allowed',
  'expanded',
  'collapsed',
])

/**
 * Stems that may carry a measurement or flag suffix.
 *
 * `merchant` is here and `merchant_name` deliberately is not: a COUNT of
 * merchants is a number, a merchant NAME is a merchant name with a word
 * appended. That distinction is the one both deny-lists got wrong.
 */
export const ENTITY_STEMS = new Set([
  'merchant',
  'merchants',
  'category',
  'categories',
  'account',
  'accounts',
  'transaction',
  'transactions',
  'tag',
  'tags',
  'query',
  'search',
  'filter',
  'filters',
])

/**
 * Stems that may carry a measurement or flag suffix.
 *
 * Those in ENTITY_STEMS above may carry ONLY those two, never an enum suffix.
 * The stem x ENUMS product otherwise manufactures a name-shaped slot for
 * precisely the things CLAUDE.md names: `merchant_name` was refused while
 * `merchant_type` was admissible, and the value behind `merchant_type` accepts a
 * merchant slug. The defence was about the key; the value slot was the leak.
 * Found by web-architect probing the registry.
 */
export const STEMS = new Set([
  'duration',
  // collections the UI counts
  'total',
  'result',
  'results',
  'row',
  'rows',
  'item',
  'items',
  'page',
  'column',
  'columns',
  'account',
  'accounts',
  'transaction',
  'transactions',
  'merchant',
  'merchants',
  'category',
  'categories',
  'budget',
  'budgets',
  'rule',
  'rules',
  'tag',
  'tags',
  'report',
  'reports',
  'insight',
  'insights',
  'widget',
  'panel',
  'series',
  'chart',
  'filter',
  'filters',
  'selection',
  'field',
  'fields',
  'needs_review',
  'pending',
  'overdue',
  // things the UI measures
  'query',
  'search',
  'error',
  'errors',
  'retry',
  'retries',
  'render',
  'load',
  'request',
  'network_latency',
  'session',
  'conversion',
  'sync',
  'step',
  'gate',
  'upgrade',
])

/**
 * Final segments that make a key an ENUM of its stem rather than data.
 *
 * `account_type` and `account_status` are the canonical M2 enums and were
 * rejected until this existed — a registry gap, not a judgement. None of these
 * words can carry data itself, so `merchant_type` is admissible while
 * `merchant_name` remains refused: `name` is not here and never will be.
 */
export const ENUMS = new Set([
  'type',
  'status',
  'kind',
  'mode',
  'state',
  'variant',
  'source',
  'method',
  'direction',
  'reason',
  'range',
  'period',
])

/**
 * Keys admissible on their own — enums and identifiers with no measurement.
 *
 * Their VALUES are still checked: an allow-listed key carrying "Save-On-Foods"
 * is rejected on the value, so the two gates are independent and a mistake in
 * this list is not on its own a leak.
 */
/**
 * Naming conventions this registry imposes, worth reading before adding a key:
 *
 *   - a flag is a SUFFIX, so `gate_shown`, not `is_premium`
 *   - a measurement is a suffix too, so `sync_days`, not `days_since_sync`
 *   - a duration is a measurement of `duration`, so `duration_ms`
 *
 * These are not gaps. The shape is the guard, so a property has to be named the
 * way the guard reads names — and the rejection message says which.
 *
 * **An absent value is allowed anywhere, and means "not measured".** So
 * `emit('transactions_viewed', { total_count: data?.length })` is fine on the
 * loading pass — it does not throw, and it does not have to be written
 * `?? 0`.
 *
 * This was decided against a reviewer's suggestion and the reasoning is worth
 * keeping, because the suggestion was the safer-looking one. `web-architect`
 * proposed that measurements and flags be REQUIRED — absent rejected, with a
 * convention of `?? 0` / `?? false` — on the grounds that it is stricter and
 * therefore not leakier. Both true. It was still the wrong call:
 *
 *   - `0` means "measured, and there were none". `null` means "we did not
 *     know yet". Collapsing the second into the first does not lose a datum,
 *     it fabricates one, and no dashboard downstream can tell the two apart
 *     afterwards.
 *   - `web-tester` had `total_count: null` on its list of calls that must keep
 *     working, and found the rejection as a live regression the same hour it
 *     shipped.
 *   - the strict form was also inconsistent: `error_code: undefined` passed
 *     while `total_count: undefined` threw, because only measurements and flags
 *     were type-checked. Absent now behaves the same under every key.
 *
 * The `?? 0` convention would have been a guard quietly changing what the app
 * reports, which is the one failure mode a privacy guard has no licence for.
 */
export const STANDALONE = new Set([
  'sort_order',
  'source',
  'surface',
  'method',
  'mode',
  'kind',
  'status',
  'variant',
  'trigger',
  'direction',
  'range',
  'period',
  'view',
  'breakpoint',
  'theme',
  'entitlement',
  'reason_code',
  'error_code',
  // The aggregator the SERVER picked (CLAUDE.md: never hard-code Plaid or
  // Flinks), which is exactly the thing worth measuring about it.
  'provider',
  // Spelled out rather than manufactured by stem x ENUMS — `account` is an
  // entity stem, so `account_type` cannot be formed by the product rule.
  'account_type',
  'account_status',
  // M1 authentication, spelled out for the same reason and after both review
  // agents reached it independently. The plan registers `mfa` and `factor` as
  // STEMS instead, which reads as the restrained choice and is the wider one: a
  // stem is a PRODUCT with MEASUREMENTS, FLAGS and ENUMS, so those two words
  // admit 74 keys, 26 of them carrying an arbitrary token. `factor_bucket` is
  // the one that matters — `_bucket` is the measurement exempt from the numeric
  // rule, so it is a label slot, and the only human-facing attribute of a
  // factor is `MfaFactor.hint`, a masked phone number. That is precisely the
  // argument ENTITY_STEMS' own docstring makes about `merchant_bucket`.
  //
  // Named in full, these two open exactly two keys and no product at all. The
  // value rules still apply on top: `used` is in FLAGS, so `mfa_used` must be a
  // boolean, and a factor hint cannot ride in on it.
  'mfa_used',
  'factor_type',
  // The notifications drawer's headline number. Spelled out rather than made
  // by adding `unread` to STEMS: a stem is a PRODUCT with MEASUREMENTS, FLAGS
  // and ENUMS, so that word would register ~37 keys to get the one wanted —
  // the same argument `mfa_used` and `factor_type` above are settled by.
  // `count` is already a MEASUREMENT, so the numeric value rule still applies.
  'unread_count',
  // ISO codes. web-architect's point is the one that decided this: a developer
  // who cannot send `currency: 'CAD'` renames the KEY, and a guard that trains
  // people to route around it is worse than the ceiling it replaced. RevenueCat
  // and M2 both want this one.
  'currency',
  'locale',
  'country',
  'language',
  // M11 Categories, spelled out for the same reason as `unread_count` above: a
  // stem is a PRODUCT with MEASUREMENTS, FLAGS and ENUMS, so adding `custom` as
  // a stem would register far more than the one key wanted, and adding `color`
  // or `icon` would register ~37 keys each to get one — `color_name` among
  // them, which is exactly the leak this file exists to refuse. `count` is
  // already a MEASUREMENT, so the numeric rule still applies to
  // `custom_categories_count`. `entity` is dropped entirely: `entitlement:
  // 'custom_categories'` (already STANDALONE, above) already identifies the
  // gate, so a second key naming the same thing would be redundant.
  //
  // `custom_color_used` / `custom_icon_used`, not `had_custom_color` / `had_-
  // custom_icon` — fix round 1. `assertKeyRegistered` treats every STANDALONE
  // entry alike, so registering the `had_*` spelling made the KEY admissible
  // but bought no VALUE enforcement: `assertValueSafe` reads a key's own final
  // segment independent of which path registered it, and `color`/`icon` are in
  // neither `MEASUREMENTS` nor `FLAGS`, so `had_custom_color: 'red'` did not
  // throw. Ending on `used` — already a `FLAGS` suffix, same as `mfa_used`
  // above — routes both keys THROUGH the boolean check rather than around it,
  // so a future refactor that passed the real colour string under this key
  // would be caught here rather than relying on today's call sites happening
  // to pass literal booleans.
  'custom_categories_count',
  'custom_color_used',
  'custom_icon_used',
  // M12 Household. Spelled out rather than made by adding `member` and `invite`
  // to STEMS: a stem is a PRODUCT, so those two words would open ~74 keys to
  // get the seven below, several of them arbitrary-token slots. A member's
  // email and display name are the things that must never travel, and a
  // spelled-out list cannot accidentally admit `member_bucket`.
  //
  // `role`, `from_role` and `to_role` are enums over exactly four values and
  // carry no user text — `HouseholdRole`, not a name.
  //
  // Fix round 2 (issue 319 blocking finding B1). `days_since_sent` and
  // `member_count_before` were both registered as STANDALONE, which
  // `assertKeyRegistered` treats identically to every other entry — but
  // `assertValueSafe` (`analytics.ts`) reads a key's own FINAL SEGMENT,
  // independent of which path admitted it, and neither `sent` nor `before`
  // is in `MEASUREMENTS`. So the key was admissible while the number check
  // that should have applied to it never did: `days_since_sent: '2026-08-
  // 01'` (a date, as a string) and `member_count_before: 'alice'` (a name)
  // both passed. The same lesson `custom_color_used` already records three
  // blocks above, missed again on the way in.
  //
  //   - `days_since_sent` -> `invite_age_days`. `days` is in `MEASUREMENTS`,
  //     so ending on it routes the value through the numeric check the key
  //     always meant to have.
  //   - `member_count_before` is dropped outright rather than renamed.
  //     `household_left` is the only event carrying it, and the "before" is
  //     already the event NAME — a second key restating it buys nothing and
  //     is one more place to get the ending wrong. `member_count` (already
  //     registered, two lines up) is what `household_left` emits instead.
  'role',
  'from_role',
  'to_role',
  'member_count',
  'pending_invite_count',
  'invite_age_days',
  // M5 accounts/link-funnel (#399). Only the counts with no existing home —
  // `account_count`/`error_count` already register through the `account`/
  // `error` STEMS above and need nothing here.
  //
  //   - `connected_count`/`manual_count`: `connected` and `manual` are not
  //     STEMS, and adding either would be the ~37-key cost this file's own
  //     opening docblock warns against for a single count apiece. Spelled
  //     out whole, ending on `count` (already a MEASUREMENT) so the numeric
  //     rule still applies — same shape as `member_count`/`unread_count`
  //     above, not `had_custom_color`'s mistake.
  //   - `accounts_added_count`: the spec's own property is `accounts_added`;
  //     `added` is neither a MEASUREMENT nor a FLAG, so a bare
  //     `accounts_added` would register the KEY (directly, here) while
  //     buying no numeric enforcement — `custom_color_used`'s fix-round-1
  //     mistake, repeated. Renamed to end on `count` for the same reason
  //     `invite_age_days` was renamed off `days_since_sent` above.
  //   - `entry_point`/`scope`: single concepts with no existing key,
  //     `link_started`'s and `sync_triggered`'s own enums.
  //
  // Deliberately NOT added: a `reason` key for `link_abandoned` (the
  // already-registered, previously-unused `reason_code` carries it — the
  // guard's own "one key per concept" argument, not a second name for the
  // same thing); a `type` key for `manual_account_added` (the already-
  // registered `account_type` carries it, and a bare `type` cannot register
  // at all — no STEM has an empty name); a `gate` key for
  // `premium_gate_shown` (the already-registered `entitlement` carries it,
  // same as `locked_create_shown`'s identical property); a `region` key for
  // `link_started`/`link_abandoned` (the region IS a country code —
  // `region.ts`'s own `REGIONS` are `'CA'`/`'US'` — so it travels as
  // `country`, already `STANDALONE` and already an `ISO_KEYS` entry, rather
  // than a second capitalised-value exemption for the same shape); and a
  // `was_manual` flag for `account_removed` (`method` already says `delete`
  // for a hand-created account and `hide` for a linked one, so a boolean would
  // restate it, and no existing FLAGS suffix fits "manual" without inventing
  // one, which costs the same as a new STEM).
  'connected_count',
  'manual_count',
  'accounts_added_count',
  'entry_point',
  'scope',
  // M11 Auto-rules. `rule_count`, `transaction_count`, `error_count`,
  // `item_count` and the enums `surface`/`method` already register through
  // existing stems, so those are used as they are. Five keys have no home:
  //
  //   - `condition_count`, `group_count`, `action_count`: the rule builder's
  //     own vocabulary. Borrowing `filter_count`/`field_count` would make the
  //     dashboard series lie about what it counts, and adding `condition`,
  //     `group` or `action` as STEMS is the ~37-key cost this file warns of
  //     for one key apiece. Spelled out, each ending on `count` so the numeric
  //     rule still applies (the `connected_count` shape).
  //   - `apply_to_past_enabled`: a flag, and ending on `enabled` routes the
  //     value through the boolean check — a bare `apply_to_past` would
  //     register the key and enforce nothing (`custom_color_used`'s lesson).
  //   - `at_least_shown`: whether the backfill count read "at least N". Ends on
  //     `shown` for the same reason.
  //   - `truncation_shown`: whether an apply's result said the run stopped before
  //     the end of its window (`rule_applied`). Not `at_least_shown`: Apply now's
  //     cut-short copy says where it stopped, never "at least". A flag by its
  //     `shown` suffix.
  'condition_count',
  'group_count',
  'action_count',
  'apply_to_past_enabled',
  'at_least_shown',
  'truncation_shown',
  //   - `resume_used`: whether this apply continued a run cut short (`resume_after`
  //     sent; Continue in Apply now, Run again in Apply all rules). A boolean,
  //     never the cursor. Spelled out because `resume` as a stem is a product
  //     (~37 keys) for this one.
  'resume_used',
  // M6 transactions (Q16). The PRD's `filter_type` and `action` cannot pass the guard:
  // `filter` is an ENTITY stem, so an enum suffix on it is refused on purpose, and `action`
  // is no stem. Spelled out whole, because a stem is a product (~37 keys) and these two are
  // all that is wanted. `kind` is in ENUMS, so the value rule is the enum rule, never free text.
  // Every other M6 key (`field_kind`, `error_kind`, `item_index`, `transaction_count`,
  // `error_count`, `item_count`, `filter_count`) is admissible through the stems already there.
  'filter_kind',
  'action_kind',
  // M5 reconnect (issue 534). Spelled out, never by adding `relink`, `paired` or
  // `partial` as STEMS — a stem is a PRODUCT (~37 keys each). The shared lesson of
  // `custom_color_used` applies: each ends on a suffix the value rules already read,
  // so the key cannot be admitted while its value goes unchecked.
  //
  //   - `paired_count`, `kept_separate_count`, `undecided_count`: how many rows the
  //     user matched, marked closed and left unanswered. Counts, ending on `count`.
  //   - `relink_status`: the reconnect's own `RelinkStatus` — a closed value set
  //     (`ENUM_VALUES`), so an identifier-shaped bank slug is refused on the VALUE.
  //   - `relink_error_kind`: why a reconnect or a save stopped — closed likewise. Not
  //     `error_kind`, whose value set is the transactions autosave's and is a TYPE
  //     (`SaveFailure`) other code derives from.
  //   - `partial_applied`: whether an earlier batch of a save was kept before it
  //     failed. A flag by its `applied` suffix, so only a boolean passes; a bare
  //     `partial` would register the key and enforce nothing.
  //
  // Never admitted, and pinned by a test: an item id, an account id, an institution or
  // account name, a mask, a balance, an overlap.
  'paired_count',
  'kept_separate_count',
  'undecided_count',
  'relink_status',
  'relink_error_kind',
  'partial_applied',
  // M7 Recurring (Task 16). Spelled out, never by adding `active`, `late`,
  // `suggestion` or `linked` as STEMS: a stem is a PRODUCT (~37 keys each). Each
  // ends on a suffix the value rules already read, so no key is admitted while its
  // value goes unchecked (`custom_color_used`'s lesson):
  //
  //   - `active_count`: the PRD's own key. `active` is a FLAG, not a stem, so the
  //     product rule cannot form it; `count` makes the value numeric.
  //   - `late_days`: the PRD's `days_late`, whose final segment `late` is no
  //     MEASUREMENT, so it would be admitted unchecked. Ending on `days` routes it
  //     through the whole-number rule (`invite_age_days`'s rename).
  //   - `suggestion_count`: the PRD's bare `count`, which has no stem at all.
  //   - `linked_count`: match-history's result, how many past payments it linked.
  //
  // The rest of M7's keys pass as they stand: `overdue_count` (`overdue` x `count`),
  // `overdue_shown` and `transaction_present` (stem x FLAG; the PRD's `was_overdue`
  // and the brief's `has_transaction`, renamed onto a flag suffix so only a boolean
  // passes), `item_count`, `source` and `direction`.
  'active_count',
  'late_days',
  'suggestion_count',
  'linked_count',
  // M3 Cashflow. `period` is already here; the PRD's `from`/`to` are not, and
  // `period` is no STEM, so the product rule cannot form them. Spelled out, each
  // ending on a suffix the value rules read (`from_role`/`to_role`'s precedent):
  // `period` is in ENUMS.
  'from_period',
  'to_period',
  // `cashflow_detail_opened`: which side of a row was opened, `in` or `out`. No STEM
  // (a stem is a product of ~37 keys); closed in ENUM_VALUES below.
  'side',
  // M4 Budget (plan P1). The PRD's spellings are refused or escape the value check
  // (`days_remaining`, `paused`, `has_category`, a bare `filter`), and `field_kind` /
  // `filter_kind` carry M6's closed sets. Spelled out like `unread_count`; each of the
  // three counts ends on a MEASUREMENTS suffix so the numeric check still applies, and
  // the two enums are closed in ENUM_VALUES below.
  'envelope_count',
  'over_count',
  'remaining_days',
  'budget_filter',
  'budget_field',
  // M2 Insight card. `severity` is closed in ENUM_VALUES; `insight_count` ends on a
  // MEASUREMENTS suffix so the numeric check applies.
  'severity',
  'insight_count',
  // M8 Reports. Which builder control changed; closed in ENUM_VALUES below. A bare `control` has no
  // stem, so the product rule cannot form it, and a stem would cost ~37 keys for this one.
  'control',
  // M8 Reports phase B. The rows dimension (a closed enum) and whether the owner pivot is on. Neither has a
  // stem, and `owner_columns_enabled` ends on `enabled`, so its value is checked as a boolean.
  'dimension',
  'owner_columns_enabled',
  // M13 `learn_from_edits_changed`: the new value of the switch. Ends on `enabled`, so only a boolean passes.
  'learn_from_edits_enabled',
  // M13 `devices_viewed`. `device_count` ends on `count` (a MEASUREMENT, numeric rule applies, the
  // `member_count` precedent); `current_device_present` ends on `present` (a FLAG, so boolean only).
  'device_count',
  'current_device_present',
  // M5 statement day and cycles. `statement_day_present` is a FLAG (ends on `present`, so boolean
  // only) and `cycle_count` a MEASUREMENT (ends on `count`); neither `statement_day` nor `cycle` is
  // a stem, because a stem is a PRODUCT (~37 keys) and these are the only two wanted. The day
  // number itself is never sent.
  'statement_day_present',
  'cycle_count',
  // M13 `settings_section_viewed`: which Settings destination. Closed in ENUM_VALUES below.
  'section',
  // M13 notifications: which kind was switched (closed in ENUM_VALUES, derived from the label map), its new state
  // (ends on `enabled`, so only a boolean passes), and the summary cadence (closed below).
  'notification_kind',
  'notification_enabled',
  'frequency_kind',
  // `report_exported`'s result. Closed in ENUM_VALUES, so an error message cannot ride in.
  'outcome',
])

/**
 * The only keys whose values may carry capitals.
 *
 * `ISO_CODE` was originally applied to every value, so any registered key
 * accepted a capitalised two-to-three-letter token — and "letters only, at most
 * eight characters" is equally the shape of a first name: `status: 'Bob'`
 * passed. web-tester found it. The exemption exists for four keys and now
 * applies to exactly those four.
 */
export const ISO_KEYS = new Set(['currency', 'locale', 'country', 'language'])

/**
 * The values an enum-valued M6 key may carry, whole.
 *
 * `kind` is in ENUMS and these keys are STANDALONE or `field`/`error` x `kind`, so the generic value
 * rule only asks for an identifier, and a lower-case merchant slug (`save-on-foods`) is one. Only the
 * emit site's TypeScript union stood between that and the sink, which is no protection through a cast
 * or a value assembled from state. The guard checks these at runtime, per key, in `emit` itself; the
 * route types derive from this table so the two cannot drift.
 */
export const ENUM_VALUES = {
  // M13; the generated schema's switchable types. `notificationPreferences.test.ts` pins this list to KIND_LABELS,
  // so the two cannot drift (this file imports nothing, so it cannot derive it).
  notification_kind: [
    'large_transaction',
    'suspicious_transaction',
    'transactions_synced',
    'recurring_paid',
    'recurring_due_soon',
    'recurring_missed',
    'relink_reminder',
    'budget_period_reset',
    'spending_summary',
  ],
  frequency_kind: ['weekly', 'monthly'],
  // M13; one value per `SETTINGS_NAV` entry.
  section: ['profile', 'preferences', 'security', 'devices', 'billing', 'developer'],
  filter_kind: ['month', 'account', 'category', 'owner', 'tag', 'amount', 'pending', 'review'],
  field_kind: ['category', 'tags', 'merchant', 'owner', 'note'],
  action_kind: ['set_category', 'set_merchant', 'add_tag', 'assign_owner'],
  error_kind: ['rejected', 'not_found', 'unauthorised', 'server', 'network'],
  // The one `account_type` vocabulary — iOS's `AccountType` raw values, per the cross-platform
  // contract (`account_type_from_wire`). A raw wire type (`depository`) is not a member, so it
  // cannot be emitted again. `method` is deliberately NOT closed here: it is shared by auth
  // (`email`/`google`), rule reordering (`buttons`/`drag`) and `account_removed` (`hide`/`delete`).
  account_type: ['checking', 'savings', 'credit', 'loan', 'investment', 'mortgage', 'cash', 'other'],
  // M5. `connect_page` is the link funnel's one entry; the other three are the reconnect's.
  entry_point: ['connect_page', 'connections', 'accounts_panel', 'link_prompt'],
  relink_status: ['pending', 'completed', 'needs_review', 'institution_mismatch'],
  // `RelinkError['kind']` ∪ the reconnect flow's own failures ∪ `unconfirmed` (a save whose POST got no answer).
  relink_error_kind: [
    'not_found',
    'use_reauth',
    'already_relinked',
    'session_mismatch',
    'provider_mismatch',
    'nonce_invalid',
    'already_linked',
    'account_changed',
    'already_paired',
    'relink_completed',
    'currency_mismatch',
    'too_many',
    'other',
    'nonce_unavailable',
    'mfa_required',
    'no_adapter',
    'abandoned',
    'link_failed',
    'unexpected',
    'unconfirmed',
  ],
  // M3 Cashflow. Closed sets, so a slug-shaped string cannot ride in on `period`.
  // The key carried no value set before and no other event emits it.
  period: ['month', 'week', 'quarter', 'year'],
  // M4 Budget. Closed sets (the envelope filter and the editable fields).
  budget_filter: ['all', 'over', 'close', 'untouched'],
  budget_field: ['name', 'category', 'amount', 'alert_threshold'],
  // `budget_detail_viewed`: the envelope's status. Closed, so an envelope or category name cannot ride in.
  budget_state: ['on_track', 'close', 'over', 'untouched', 'paused'],
  from_period: ['month', 'week', 'quarter', 'year'],
  to_period: ['month', 'week', 'quarter', 'year'],
  panel_kind: ['chart', 'categories', 'insight', 'report'],
  // `cashflow_breakdown_view_changed`: the Where it went toggle. Closed, so a slug cannot ride in on `view`.
  view: ['categories', 'groups', 'merchants'],
  // `scope` is shared: `sync_triggered` sends `item`/`all`, `cashflow_detail_opened` a
  // drill-down kind, `device_signed_out` `one`/`all`, `transaction_deleted` `one`/`bulk`. Closed over all,
  // so none can carry a merchant slug.
  scope: ['item', 'one', 'all', 'bulk', 'category', 'group', 'merchant', 'payer', 'uncategorised', 'no_merchant'],
  side: ['in', 'out'],
  // `insight_shown`: the server's severity. Closed, so insight text cannot ride in.
  severity: ['info', 'warn', 'alert'],
  // M8 Reports. `report_config_changed`'s control, and `report_run`'s date-range preset. Closed, so a date
  // or a slug cannot ride in on `range`. No other event emits either key.
  control: ['range', 'columns', 'filter', 'chart_type', 'reset', 'rows', 'preset'],
  // M8 Reports: `report_run`'s rows dimension. The word only; never an id.
  dimension: ['category', 'group', 'merchant', 'owner', 'account', 'tag'],
  range: ['mtd', 'ytd', '12m', 'custom'],
  outcome: ['success', 'too_many_periods', 'failed'],
} as const satisfies Record<string, readonly string[]>
